import { Component, computed, effect, inject, input } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { AbstractNewSurveyComponent } from '@unity/abstract/abstract-new-survey.component';
import {
  CreatedResponseDto,
  FormDataDto,
  FormResponseCreateDto,
  FormResponsesClient,
  FormSchedulesClient,
  TransitionMiniDto,
} from '@unity/clients';
import { PageContainerComponent } from '@unity/components/layouts/page-container/page-container.component';
import { IconService } from '@unity/services/icon.service';
import { SurveyDataService } from '@unity/services/survey-data.service';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSurveySharedComponent } from '@unity/shared/composed/card-new-survey-shared/card-new-survey-shared.component';
import { QuickActions } from '@unity/utils/quick-actions';
import { UnityRouteUrlTrees } from '@unity/utils/types';
import { EMPTY } from 'rxjs';
import { SurveyModule } from 'survey-angular-ui';

@Component({
  selector: 'unity-new-form-response-page',
  imports: [KENDO_BUTTON, KENDO_STACKLAYOUT, SurveyModule, PageContainerComponent, ActionSharedComponent, CardNewSurveySharedComponent],
  templateUrl: './new-form-response-page.component.html',
  styleUrl: './new-form-response-page.component.scss',
})
export class NewFormResponsePageComponent extends AbstractNewSurveyComponent<CreatedResponseDto> {
  /* Dependencies */
  private readonly formSchedulesClient = inject(FormSchedulesClient);
  private readonly formResponsesClient = inject(FormResponsesClient);
  private readonly iconService = inject(IconService);
  private readonly surveyDataService = inject(SurveyDataService);

  protected saveIcon = this.iconService.saveIcon;

  /* Inputs */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });
  public readonly $formScheduleId = input.required({ alias: 'formScheduleId', transform: (value: string) => parseInt(value) });
  public readonly $formInstanceId = input(undefined, { alias: 'formInstanceId', transform: (value: string) => parseInt(value) });

  /* Impl */
  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  protected override redirectUrlFn = (_: CreatedResponseDto): UnityRouteUrlTrees => {
    const agreementId = this.$agreementId();
    const formScheduleId = this.$formScheduleId();
    return ['/agreement', agreementId, 'form-schedule', formScheduleId, 'form'];
  };

  protected override cancelUrlFn = (): UnityRouteUrlTrees => {
    const agreementId = this.$agreementId();
    const formScheduleId = this.$formScheduleId();
    return ['/agreement', agreementId, 'form-schedule', formScheduleId, 'form'];
  };

  protected override readonly $formSchema = computed(() => {
    const formSchedule = this.formScheduleResource.value();
    return formSchedule?.form_schema;
  });

  /* Resources */
  protected readonly formScheduleResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      const formScheduleId = this.$formScheduleId();
      return agreementId && formScheduleId ? { agreementId, formScheduleId } : undefined;
    },
    stream: ({ params: { agreementId, formScheduleId } }) =>
      this.formSchedulesClient.getOneForAgreementAsDetailed(agreementId, formScheduleId),
  });

  /* Signals */
  protected readonly $contentHeader = computed(() => {
    const formScheduleResource = this.formScheduleResource.value();
    return `Create ${formScheduleResource?.name}`;
  });

  protected readonly $contentHeaderSecondary = computed(() => {
    const formScheduleResource = this.formScheduleResource.value();
    return `Agreement: ${formScheduleResource?.agreement.name}`;
  });

  public constructor() {
    super();

    effect(() => {
      const agreementId = this.$agreementId();
      this.surveyDataService.setAgreementId(agreementId);
    });
  }

  /* Methods */
  protected override createResourceFn$ = (data: FormDataDto, transition: TransitionMiniDto) => {
    const agreementId = this.$agreementId();
    const formScheduleId = this.$formScheduleId();
    const formInstanceId = this.$formInstanceId();

    if (!agreementId || !formScheduleId) {
      return EMPTY;
    }

    const createDto: FormResponseCreateDto = {
      agreement_id: agreementId,
      form_schedule_id: formScheduleId,
      form_instance_id: formInstanceId,
      initial_transition_id: transition.id,
      data: data,
    };

    return this.formResponsesClient.createOneForAgreement(agreementId, createDto);
  };
}
